import { AccessTokenProvider, AllowedHostsValidator, AuthenticationProvider, RequestInformation, Parsable, ParsableFactory, ErrorMappings, AnonymousAuthenticationProvider, type RequestAdapter } from '@microsoft/kiota-abstractions';
import router from '@/router';
import { useAuthStore } from '@/stores/authStore';
import { BaseBearerTokenAuthenticationProvider } from '@microsoft/kiota-abstractions';
import { FetchRequestAdapter } from '@microsoft/kiota-http-fetchlibrary';
import { createApiClient } from '@/api-client/apiClient';

/**
 * Reads `org_codes` from the current access token and returns it JSON-stringified
 * for the `OrgCodes` HTTP header. Required by endpoints declaring
 * `[FromHeader("OrgCodes")]` (Invoices, Payments). Falls back to `[org_code]` or
 * `[]` so the header is always present.
 *
 * Exported for plain-fetch callers (binary downloads bypass the Kiota adapter,
 * which otherwise adds this header via OrgCodesAwareAuthenticationProvider).
 */
export function getOrgCodesHeader(): string {
    const token = localStorage.getItem('auth_token') ?? '';
    if (!token || token === 'undefined') return '[]';
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return '[]';
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '==='.slice((base64.length + 3) % 4);
        const payload = JSON.parse(atob(padded));
        if (Array.isArray(payload.org_codes)) return JSON.stringify(payload.org_codes);
        if (typeof payload.org_codes === 'string') return JSON.stringify([payload.org_codes]);
        if (typeof payload.org_code === 'string') return JSON.stringify([payload.org_code]);
        return '[]';
    } catch {
        return '[]';
    }
}

export class ApiClientFactory {
    private static instance: ApiClientFactory;
    private static client: ReturnType<typeof createApiClient>;
    private static adapter: RequestAdapter;
    private authStore = useAuthStore();
    
    private constructor() {}
    
    private async handleAuthenticationError() {
        this.authStore.clearAuthData();
        await router.push('/');
    }

    static getInstance(): ApiClientFactory {
        if (!ApiClientFactory.instance) {
            ApiClientFactory.instance = new ApiClientFactory();
        }
        return ApiClientFactory.instance;
    }
    
    getClient(): ReturnType<typeof createApiClient> {
        if (!ApiClientFactory.client) {
            const accessTokenProvider = new AuthStoreAccessTokenProvider(this.authStore);
            const bearerAuthProvider = new BaseBearerTokenAuthenticationProvider(accessTokenProvider);
            const authProvider = new OrgCodesAwareAuthenticationProvider(bearerAuthProvider);
            const adapter = new FetchRequestAdapter(authProvider);
            adapter.baseUrl = process.env.VITE_MOMENTUM_API_BASE_URL || '';

            // Add response interceptor to handle 401 responses
            const originalSend = adapter.send.bind(adapter);
            adapter.send = async <T extends Parsable>(requestInfo: RequestInformation, deserializer: ParsableFactory<T>, errorMappings?: ErrorMappings): Promise<T> => {
                try {
                    const response = await originalSend(requestInfo, deserializer, errorMappings);
                    if (!response) {
                        throw new Error('No response received from server');
                    }
                    return response;
                } catch (error: any) {
                    if (error.status === 401) {
                        await ApiClientFactory.instance.handleAuthenticationError();
                        // Mark the error as handled
                        error.handled = true;
                    }
                    throw error;
                }
            };

            ApiClientFactory.adapter = adapter;
            ApiClientFactory.client = createApiClient(adapter);
        }

        return ApiClientFactory.client;
    }

    /**
     * The authenticated adapter behind the client, for a route the generated client does not have
     * yet (an endpoint merged ahead of a Kiota regeneration). Same bearer token, OrgCodes header and
     * middleware as every generated call; the generated proxy itself never exposes it.
     */
    getRequestAdapter(): RequestAdapter {
        this.getClient();
        return ApiClientFactory.adapter;
    }

    getUnauthenticatedClient(): ReturnType<typeof createApiClient> {
        // Create a client without authentication for public endpoints
        const authProvider = new AnonymousAuthenticationProvider();
        const adapter = new FetchRequestAdapter(authProvider);
        adapter.baseUrl = process.env.VITE_MOMENTUM_API_BASE_URL || '';
        return createApiClient(adapter);
    }
}

/**
 * Wraps the bearer-token auth provider so every Kiota request (regardless of which
 * adapter send-variant is used) gets the `OrgCodes` HTTP header. Required by
 * endpoints declaring `[FromHeader("OrgCodes")]` (Invoices, Payments).
 */
class OrgCodesAwareAuthenticationProvider implements AuthenticationProvider {
    constructor(private readonly inner: AuthenticationProvider) {}

    async authenticateRequest(request: RequestInformation, additionalAuthenticationContext?: Record<string, unknown>): Promise<void> {
        await this.inner.authenticateRequest(request, additionalAuthenticationContext);
        request.headers.tryAdd('OrgCodes', getOrgCodesHeader());
    }
}

class AuthStoreAccessTokenProvider implements AccessTokenProvider {
    private authStore: ReturnType<typeof useAuthStore>;

    constructor(authStore: ReturnType<typeof useAuthStore>) {
        this.authStore = authStore;
    }

    async getAuthorizationToken(url?: string, additionalAuthenticationContext?: Record<string, unknown>): Promise<string> {
        // Use the getAuthorizationToken method of authStore to get the token
        return  (await this.authStore).getAuthorizationToken();
    }

    getAllowedHostsValidator(): AllowedHostsValidator {
        // Return a validator that allows all hosts since we're handling auth elsewhere
        const validator = new AllowedHostsValidator();
        validator.setAllowedHosts(new Set(['*']));
        return validator;
    }
}
